// Parser del formato RASP `.eng`. Ver docs/theory/motors.md.
import { createMotor, type Motor, type MotorInput } from './motor';

export interface MotorParseError {
  /** Línea (1-based) del archivo, si aplica. */
  readonly line?: number;
  /** Designación del motor afectado, si se conoce. */
  readonly motor?: string;
  readonly message: string;
}

export interface MotorParseResult {
  readonly motors: readonly Motor[];
  readonly errors: readonly MotorParseError[];
}

export const MAX_MOTOR_FILE_BYTES = 2_000_000;

const num = (s: string): number => (s.trim() === '' ? Number.NaN : Number(s));

function parseDelays(token: string): { delays: number[]; plugged: boolean } | undefined {
  const delays: number[] = [];
  let plugged = false;
  for (const part of token.split('-')) {
    if (/^p$/i.test(part)) plugged = true;
    else {
      const v = num(part);
      if (!Number.isFinite(v) || v < 0) return undefined;
      delays.push(v);
    }
  }
  return { delays, plugged };
}

interface Block {
  header: Omit<MotorInput, 'points'>;
  points: Array<[number, number]>;
  line: number;
}

export function parseEng(text: string): MotorParseResult {
  const motors: Motor[] = [];
  const errors: MotorParseError[] = [];
  if (text.length > MAX_MOTOR_FILE_BYTES) return { motors, errors: [{ message: 'El archivo supera el tamaño máximo permitido' }] };

  let block: Block | undefined;
  let skipping = false; // datos de una cabecera inválida

  const close = (): void => {
    if (block === undefined) return;
    const r = createMotor({ ...block.header, points: block.points });
    if (r.ok) motors.push(r.motor);
    else errors.push({ line: block.line, motor: block.header.designation, message: r.error });
    block = undefined;
  };

  text.split(/\r?\n/).forEach((raw, idx) => {
    const lineNo = idx + 1;
    const line = raw.trim();
    if (line === '' || line.startsWith(';')) return;
    const tokens = line.split(/\s+/);

    const isData = tokens.length === 2 && tokens.every((t) => Number.isFinite(num(t)));
    if (isData) {
      if (block !== undefined) block.points.push([num(tokens[0]!), num(tokens[1]!)]);
      else if (!skipping) errors.push({ line: lineNo, message: 'Dato de empuje sin cabecera de motor' });
      return;
    }

    // Cabecera de un nuevo motor.
    close();
    skipping = false;
    if (tokens.length < 6) {
      errors.push({ line: lineNo, message: `Cabecera incompleta: se esperaban al menos 6 campos y hay ${tokens.length}` });
      skipping = true;
      return;
    }
    const [name, dia, len, delayToken, prop, total, ...mfg] = tokens as [string, string, string, string, string, string, ...string[]];
    const delays = parseDelays(delayToken);
    const fields = { dia: num(dia), len: num(len), prop: num(prop), total: num(total) };
    const bad = (Object.keys(fields) as Array<keyof typeof fields>).find((k) => !Number.isFinite(fields[k]));
    if (delays === undefined || bad !== undefined) {
      errors.push({ line: lineNo, motor: name, message: delays === undefined ? `Retardos inválidos: ${delayToken}` : `Campo numérico inválido en la cabecera (${bad})` });
      skipping = true;
      return;
    }
    block = {
      header: {
        designation: name,
        manufacturer: mfg.length > 0 ? mfg.join(' ') : 'Desconocido',
        diameter: fields.dia / 1000,
        length: fields.len / 1000,
        delays: delays.delays,
        plugged: delays.plugged,
        propellantMass: fields.prop,
        totalMass: fields.total,
      },
      points: [],
      line: lineNo,
    };
  });
  close();

  return { motors, errors };
}
